import Color from 'color';
import styles from './styles.less';
import React from 'react';
import PropTypes from 'prop-types';
import { Observable, Subscription } from '@reactivex/rxjs';
import {
    pointSizes,
    pointColors,
    edgeColors,
    toggleZoomIn,
    toggleCenter,
    toggleZoomOut,
    isAnimating,
    cameraChanges,
    hitmapUpdates,
    curPoints,
    forwardsEdgeStartEndIdxs,
    forwardsEdgeToUnsortedEdge,
    logicalEdges,
} from '@graphistry/viz/src/client/legacy';

import compose from 'react-recompose/compose';
import getContext from 'react-recompose/getContext';

import { scenes } from '@graphistry/viz/src/models/scene/scenes';
import RenderingScheduler from './rendering-scheduler';
import { init as initRenderer } from './renderer-state';
import { setupRotate, setupCenter, setupZoomButton } from './interaction';

const events = ['onMouseMove', 'onTouchStart', 'onShiftDown', 'onWheel'];

let globalRenderer, globalRenderState, globalRenderingScheduler;

const DEFAULT_BG_COLOR_HEX_STR = '#323238';

class Scene extends React.Component {
    static childContextTypes = {
        renderState: PropTypes.object,
        renderingScheduler: PropTypes.object
    };
    constructor(props, context) {
        super(props, context);
        this.state = {
            renderer: undefined,
            renderState: undefined,
            renderingScheduler: undefined,
            hasDOMListeners: false,
            hasVBOListeners: false,
            renderSubscription: new Subscription(),
            bgDivStyle: {  }
        };
        this.container = undefined;
        this.simulationWidth = props.simulationWidth || 0;
        this.simulationHeight = props.simulationHeight || 0;
        this.assignRef = (container) => {
            if (this.container) {
                this.container.removeEventListener('wheel', this.onWheel, { passive: false });
            }
            if (container && (this.container = container)) {
                this.container.addEventListener('wheel', this.onWheel, { passive: false });
                this.onResize({
                    simulationWidth: container.offsetWidth,
                    simulationHeight: container.offsetHeight
                });
            }
        };
        const makeEventHandler = eventName =>
            (this[eventName] = event => {
                const { props, state } = this;
                const dispatch = props[eventName];
                const { renderState, renderingScheduler } = state;
                if (!dispatch || !renderState || !renderingScheduler) {
                    return;
                }
                const { camera } = renderState;
                const { simulationWidth, simulationHeight } = this;
                const { simulating, highlightEnabled, selection = {}, isKeplerOpen } = props;
                //hit tests are about to need the hitmap; render it now if the
                //last settle deferred it (lazy picking). Hover hit tests only
                //run with no buttons held (pickNodeSelection filters
                //event.buttons === 0), so never pay the hitmap render
                //mid-drag, where it cannot be consumed and stalls the gesture.
                //Touch taps get no hover move first, so touchstart triggers
                //it; mouse drags (mousedown routes through onTouchStart too)
                //must not.
                if ((eventName === 'onMouseMove' && !event.buttons) ||
                    (eventName === 'onTouchStart' &&
                        event.type && event.type.indexOf('touch') === 0)) {
                    renderingScheduler.renderPickingLazily();
                }
                dispatch({
                    selectionMask: selection.mask,
                    selectionType: selection.type,
                    simulationWidth, simulationHeight,
                    event, simulating, highlightEnabled,
                    camera, renderState, renderingScheduler,
                    selection,
                    isKeplerOpen
                });
            });
        events.forEach(eventName => {
            const handler = makeEventHandler(eventName);
            if (eventName === 'onShiftDown') {
                this[eventName] = event => {
                    if (!event.repeat && event.key === 'Shift') {
                        handler(event);
                    }
                };
            }
        });
    }
    onResize({ simulationWidth, simulationHeight }) {
        this.simulationWidth = simulationWidth;
        this.simulationHeight = simulationHeight;
        const { renderer, renderState = {} } = this.state;
        const { camera } = renderState;
        if (renderer && renderState && camera) {
            camera.simulationWidth = simulationWidth;
            camera.simulationHeight = simulationHeight;
            renderer.resizeCanvas(renderState);
        }
        return !!camera;
    }
    getChildContext() {
        const { renderState, renderingScheduler } = this.state;
        return { renderState, renderingScheduler };
    }
    UNSAFE_componentWillMount() {
        const { props, state } = this;
        const { sceneID } = props;
        if (sceneID && (sceneID in scenes)) {
            this.setupRenderStateAndScheduler(props, state);
        }
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { props, state } = this;
        const { sceneID: currSceneID } = props;
        const { sceneID: nextSceneID } = nextProps;
        if (!state.renderState &&
            (nextSceneID && currSceneID !== nextSceneID) &&
            (nextSceneID in scenes)) {
            this.setupRenderStateAndScheduler(nextProps, this.state);
        }

        //TODO why are these not regular render props? limiting slider change impact?
        const { bgDivStyle, backgroundColorRGB } = state;
        const { backgroundColor = DEFAULT_BG_COLOR_HEX_STR } = nextProps;
        const nextColorRGB = new Color(backgroundColor).rgb();
        if (!bgDivStyle || !backgroundColorRGB || (backgroundColorRGB.hex() != nextColorRGB.hex())) {
            this.setState({
                backgroundColorRGB: nextColorRGB,
                bgDivStyle: { backgroundColor: nextColorRGB.toString() }
            });
        }
    }
    componentDidMount() {
        // console.log('mounted scene');
        const { props, state } = this;
        const { renderer, renderState } = state;
        renderer && renderer.resizeCanvas(renderState);
        this.setupDOMAndSourceListeners(props, state);
    }
    componentDidUpdate(prevProps) {
        this.setupDOMAndSourceListeners(this.props, this.state);
        const { simulationWidth, simulationHeight } = this.props;
        if (this.simulationWidth !== simulationWidth || this.simulationHeight !== simulationHeight) {
            this.onResize({ simulationWidth, simulationHeight });
        }
    }
    componentWillUnmount() {
        // console.log('unmounting scene');
        this.assignRef = undefined;
        const { renderSubscription } = this.state;
        renderSubscription && renderSubscription.unsubscribe();
        events.forEach(eventName => (this[eventName] = undefined));
    }


    renderBackgroundGradient() {
        const { backgroundGradient = {} } = this.props;

        const { kind, position = '', stops = [] } = backgroundGradient;
        if (!kind || !(kind === 'radial' || kind === 'linear') || !stops.length) { return null; }

        //FIXME SECURITY does position need sanitization?
        const positionStr =
            typeof(position) === 'number' ? `${position}deg, `
            : position                    ? `${position}, `
            : '';

        //FIXME SECURITY does stops need sanitization?
        const stopsStr = stops.map(stop => stop.join(' ')).join(', ');

        return (<div
            key="sceneGradient"
            className={styles['scene-background-gradient']}
            style={{
                backgroundImage: `${kind}-gradient(${positionStr}${stopsStr})`
        }} />);
    }

    renderBackgroundImage() {
        const { backgroundImage = {} } = this.props;
        const { blendMode, width, height, url } = backgroundImage;
        if (!url) { return null; }

        const extraProps = {};
        if (width && height && !isNaN(width) && !isNaN(height)) {
            extraProps.backgroundSize = 'cover';
            extraProps.width = width;
            extraProps.height = height;
            extraProps.left = `calc(50% - ${width/2}px)`;
            extraProps.top = `calc(50% - ${height/2}px)`;
        }
        if (blendMode) {
            extraProps['mixBlendMode'] = blendMode;
        }

        //FIXME SECURITY does url need sanitization?
        return (<div
            key="sceneBgImage"
            className={styles['scene-background-image']}
            style={{
                ...extraProps,
                backgroundImage: `url(${url})`
        }}/>)
    }

    render() {
        const { bgDivStyle = {} } = this.state; //FIXME why state -- fast changing so do not do in regular prop pass?
        const { sceneHeight, simulationHeight, fgBlendMode, rendererComponent } = this.props;
        const { edges, points, release, children = [] } = this.props;
        const { labelPOIOption, pruneIsolatedOption } = this.props;
        const { info = true, menu = true } = this.props;
        // const releaseString = release.buildNumber ? `v${release.buildNumber}` : '';
        const infoBottomStyle = { bottom: simulationHeight - sceneHeight + 10 };

        return (
            <div
                id="simulation-container"
                ref={this.assignRef}
                style={this.props.style}
                onKeyDown={this.onShiftDown}
                onMouseMove={this.onMouseMove}
                onMouseDown={this.onTouchStart}
                onTouchStart={this.onTouchStart}>
                <div key="sceneBgColor" className={styles['scene-background-color']} style={bgDivStyle} />
                { this.renderBackgroundGradient() }
                { this.renderBackgroundImage() }
                <div className={styles['scene-render-container']}
                    style={fgBlendMode ? {'mixBlendMode': fgBlendMode} : {}}
                    key="rendererContainer">{ 
                    rendererComponent || undefined
                }</div>
                {children}
                {((info || menu) && (
                    <div className={styles['top-level-scene-info']} style={infoBottomStyle}>
                        {(info && (
                            <table className={styles['meter']}>
                                <tbody>
                                    <tr>
                                        <td className={styles['meter-label']}>nodes</td>
                                        <td className={styles['meter-value']}>
                                            {(points && points.elements) || 0}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={styles['meter-label']}>edges</td>
                                        <td className={styles['meter-value']}>
                                            {(edges && edges.elements) || 0}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        )) || undefined}
                        {(menu && (
                            <div className={styles['top-level-controls']}>
                                <div>{pruneIsolatedOption}</div>
                                <div>{labelPOIOption}</div>
                            </div>
                        )) || undefined}
                    </div>
                )) || undefined}
            </div>
        );
    }
    setupRenderStateAndScheduler(props, state) {
        let {
            renderer,
            renderState,
            renderingScheduler,
            hasVBOListeners,
            hasSourceListeners
        } = state;

        hasSourceListeners = false;

        const {
            sceneID,
            pixelRatio,
            simulation,
            simulationWidth, simulationHeight,
            simCameraBounds
        } = props;

        // console.log('initializing render state and scheduler');

        if (globalRenderer && globalRenderState && globalRenderingScheduler) {
            hasVBOListeners = true;
            renderer = globalRenderer;
            renderState = globalRenderState;
            renderingScheduler = globalRenderingScheduler;
        } else {
            const scene = scenes[sceneID]({
                //instanced SDF edges are the default renderer; ?legacyRenderer
                //falls back to the CPU-expanded LINES pipeline
                edgesdf: !/[?&]legacyRenderer\b/.test(window.location.search)
            });
            const rendererOptions = { pixelRatio, ...simCameraBounds };
            const rendererInit = initRenderer(scene, simulation, rendererOptions);
            renderer = globalRenderer = rendererInit.renderer;
            renderState = globalRenderState = rendererInit.state;

            if (hasVBOListeners === false) {
                hasVBOListeners = true;
                renderingScheduler = globalRenderingScheduler = new RenderingScheduler(
                    renderState, renderer, hitmapUpdates, isAnimating
                );
            } else {
                renderingScheduler.renderState = renderState;
            }
        }

        this.setState(
            {
                renderer,
                renderState,
                hasSourceListeners,
                renderingScheduler,
                hasVBOListeners,
            },
            () => this.onResize({ simulationWidth, simulationHeight })
        );
    }
    setupDOMAndSourceListeners(props = {}, state = {}) {
        const { container } = this;
        const { simulation } = props;
        let {
            renderState,
            hasDOMListeners,
            hasSourceListeners,
            renderSubscription,
            cameraChangesSource
        } = state;

        if (!container || !simulation || !renderState || (hasDOMListeners && hasSourceListeners)) {
            return;
        }

        if (!renderState.gl) {
            console.error('setupDOMAndSourceListeners: no GL initialized');
            return;
        }

        hasSourceListeners = true;

        renderSubscription.unsubscribe();

        if (hasDOMListeners === false) {
            hasDOMListeners = true;

            const { camera } = renderState;
            const centerSource = setupCenter(toggleCenter, camera);
            const zoomInSource = setupZoomButton(toggleZoomIn, camera, 1 / 1.25);
            const zoomOutSource = setupZoomButton(toggleZoomOut, camera, 1.25);
            const rotateSource = setupRotate(container, camera);

            cameraChangesSource = Observable.merge(
                rotateSource,
                centerSource,
                zoomInSource,
                zoomOutSource
            );
        }

        renderSubscription = Observable.merge(
            // Subscribe the global Subjects to their legacy sources.
            // Eventually we need to refactor the render loop to work
            // within the React component lifecycle, then delete this code.
            // Currently they are globals across the entire app.
            cameraChangesSource.do(cameraChanges),
            renderState.hostBuffers.curPoints.do(curPoints),
            renderState.hostBuffers.pointSizes.do(pointSizes),
            renderState.hostBuffers.pointColors.do(pointColors),
            renderState.hostBuffers.edgeColors.do(edgeColors),
            renderState.hostBuffers.forwardsEdgeStartEndIdxs.do(forwardsEdgeStartEndIdxs),
            renderState.hostBuffers.forwardsEdgeToUnsortedEdge.do(forwardsEdgeToUnsortedEdge),
            renderState.hostBuffers.logicalEdges.do(logicalEdges)
        ).ignoreElements().subscribe({});

        this.setState({
            hasDOMListeners,
            hasSourceListeners,
            renderSubscription,
            cameraChangesSource
        });
    }
}

const SceneWrapped = compose(
    getContext({
        info: PropTypes.bool,
        menu: PropTypes.bool,
        play: PropTypes.number,
        pixelRatio: PropTypes.number,
        simulation: PropTypes.object
    })
)(Scene);

export { SceneWrapped as Scene };
